import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { getAdminStats, fetchUserStatus, setImpersonateUid, saveToKnownWinners } from '../api/api';
import { LogOut, HelpCircle, X, Shield, Save } from 'lucide-react';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

interface UserProfileProps {
  onHelpClick?: () => void;
  knownWinners?: string[];
}

const UserProfile: React.FC<UserProfileProps> = ({ onHelpClick, knownWinners }) => {
  const { currentUser, logout, isPro, isSuperuser, twitterHandle, impersonatingUid, setImpersonatingUid } = useAuth();
  const [godModeInput, setGodModeInput] = useState('');
  const [godModeError, setGodModeError] = useState('');
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [stats, setStats] = useState<any>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [saveKWLoading, setSaveKWLoading] = useState(false);
  const [saveKWStatus, setSaveKWStatus] = useState<'success' | 'error' | null>(null);

  useEffect(() => {
    if (dropdownOpen && isSuperuser && !stats) {
      setStatsLoading(true);
      getAdminStats()
        .then(setStats)
        .catch(() => {})
        .finally(() => setStatsLoading(false));
    }
  }, [dropdownOpen, isSuperuser, stats]);

  const handleLogout = async () => {
    try {
      await logout();
    } catch (error) {
      console.error('Failed to log out', error);
    }
  };

  if (!currentUser) {
    return null;
  }

  const displayName = currentUser.displayName || currentUser.email || 'User';
  const firstLetter = displayName.charAt(0).toUpperCase();

  return (
    <DropdownMenu open={dropdownOpen} onOpenChange={setDropdownOpen}>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" className="relative h-8 w-8 rounded-full p-0">
          <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary text-primary-foreground text-sm font-bold">
            {firstLetter}
          </div>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className="w-56" align="end" forceMount>
        <DropdownMenuLabel className="font-normal">
          <div className="flex flex-col space-y-1">
            <div className="flex items-center gap-1.5">
              <p className="text-sm font-medium leading-none">
                {currentUser.displayName || currentUser.email?.split('@')[0]}
              </p>
              {isPro && (
                <span className="rounded bg-[#1c1917] px-1.5 py-0.5 text-[10px] font-bold leading-none text-[#faf8f5]">PRO</span>
              )}
            </div>
            {(currentUser.email || twitterHandle) && (
              <p className="text-xs leading-none text-muted-foreground">
                {currentUser.email || twitterHandle}
              </p>
            )}
          </div>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        {onHelpClick && (
          <DropdownMenuItem onClick={onHelpClick}>
            <HelpCircle className="mr-2 h-4 w-4" />
            <span>Help</span>
          </DropdownMenuItem>
        )}
        <DropdownMenuItem onClick={handleLogout}>
          <LogOut className="mr-2 h-4 w-4" />
          <span>Log out</span>
        </DropdownMenuItem>
        {isSuperuser && (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="font-normal">
              <div className="flex items-center gap-1.5 text-xs text-orange-500 font-semibold">
                <Shield className="h-3 w-3" />
                God Mode
              </div>
            </DropdownMenuLabel>
            {impersonatingUid ? (
              <div className="px-2 py-1.5">
                <div className="flex items-center gap-1.5 text-xs">
                  <span className="text-muted-foreground truncate flex-1" title={impersonatingUid}>
                    {impersonatingUid}
                  </span>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setImpersonatingUid(null);
                    }}
                    className="text-muted-foreground hover:text-foreground shrink-0"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ) : (
              <div className="px-2 py-1.5">
                <input
                  type="text"
                  placeholder="Enter UID..."
                  value={godModeInput}
                  onChange={(e) => { setGodModeInput(e.target.value); setGodModeError(''); }}
                  onKeyDown={async (e) => {
                    e.stopPropagation();
                    if (e.key === 'Enter' && godModeInput.trim()) {
                      const uid = godModeInput.trim();
                      setGodModeError('');
                      // Temporarily set the impersonation header to validate
                      setImpersonateUid(uid);
                      try {
                        await fetchUserStatus();
                        setImpersonatingUid(uid);
                        setGodModeInput('');
                      } catch (err: any) {
                        setImpersonateUid(null);
                        setGodModeError(err.message === 'User not found' ? 'User not found' : 'Error validating UID');
                      }
                    }
                  }}
                  onClick={(e) => e.stopPropagation()}
                  className="w-full rounded border border-input bg-background px-2 py-1 text-xs focus:outline-none focus:ring-1 focus:ring-ring"
                />
                {godModeError && (
                  <p className="text-xs text-red-500 mt-1">{godModeError}</p>
                )}
              </div>
            )}
            {statsLoading ? (
              <div className="px-2 py-1.5 text-xs text-muted-foreground">Loading stats...</div>
            ) : stats ? (
              <div className="px-2 py-1.5">
                <div className="grid grid-cols-2 gap-x-4 gap-y-0.5 text-xs text-muted-foreground">
                  <span>Users: <span className="text-foreground font-medium">{stats.users?.total ?? '—'}</span></span>
                  <span>Today: <span className="text-foreground font-medium">{stats.users?.today ?? '—'}</span></span>
                  <span>This week: <span className="text-foreground font-medium">{stats.users?.week ?? '—'}</span></span>
                  <span>Pro: <span className="text-foreground font-medium">{stats.users?.pro ?? '—'}</span></span>
                  <span>Pools: <span className="text-foreground font-medium">{stats.pools?.total ?? '—'}</span></span>
                  <span>Today: <span className="text-foreground font-medium">{stats.pools?.today ?? '—'}</span></span>
                  <span>Entries: <span className="text-foreground font-medium">{stats.pools?.entries?.toLocaleString() ?? '—'}</span></span>
                  <span>Scenarios: <span className="text-foreground font-medium">{stats.scenarios?.total ?? '—'}</span></span>
                </div>
              </div>
            ) : null}
            {knownWinners && (
              <div className="px-2 py-1.5">
                <button
                  onClick={async (e) => {
                    e.stopPropagation();
                    if (saveKWLoading) return;
                    if (!window.confirm('Save current bracket state to known_winners.txt on the server?')) return;
                    setSaveKWLoading(true);
                    setSaveKWStatus(null);
                    try {
                      await saveToKnownWinners(knownWinners);
                      setSaveKWStatus('success');
                      setTimeout(() => setSaveKWStatus(null), 2000);
                    } catch {
                      setSaveKWStatus('error');
                      setTimeout(() => setSaveKWStatus(null), 3000);
                    } finally {
                      setSaveKWLoading(false);
                    }
                  }}
                  disabled={saveKWLoading}
                  className="flex items-center gap-1.5 text-xs text-orange-500 hover:text-orange-400 disabled:opacity-50"
                >
                  <Save className="h-3 w-3" />
                  {saveKWLoading ? 'Saving...' : saveKWStatus === 'success' ? 'Saved!' : saveKWStatus === 'error' ? 'Failed' : 'Save to Known Winners'}
                </button>
              </div>
            )}
          </>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

export default UserProfile;
